import React, { useCallback, useEffect, useState } from 'react';
import Spinner from '../../ui/Spinner';
import { documentDesignService } from '../../../services/documentDesignService';
import { mediaAssetPreviewUrl } from './mediaCreatorAssetPreview';
import { isTransparentAsset } from './mediaCreatorHtmlResolve';

const dpiClass = (dpi) => (!dpi ? 'unknown' : dpi >= 300 ? 'good' : dpi >= 200 ? 'fair' : 'low');

const MediaAssetCarousel = ({
  role = 'photo',
  preset,
  brief,
  query,
  requireApproved = false,
  selectedId,
  suggested = [],
  onSelect,
  onSearchMore,
}) => {
  const [items, setItems] = useState(suggested);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await documentDesignService.getCompositionAssetCandidates({
        role,
        preset,
        q: query || undefined,
        brief: !query ? (brief || '').slice(0, 300) || undefined : undefined,
        require_approved: requireApproved,
        limit: 24,
      });
      setItems(rows);
    } catch (err) {
      setItems(suggested);
      setLoadError(err.message || 'Could not load suggestions');
    } finally {
      setLoading(false);
    }
  }, [role, preset, brief, query, requireApproved, suggested]);

  useEffect(() => {
    if (suggested?.length && !query) {
      setItems(suggested);
      return undefined;
    }
    const t = setTimeout(load, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, query, suggested]);

  if (loading && !items.length) {
    return <Spinner size="small" />;
  }

  return (
    <div className="mc-carousel-wrap">
      {loadError && !items?.length && (
        <p className="dd-muted mc-carousel-error">{loadError}</p>
      )}
      {!loading && !loadError && !(items || []).length && (
        <p className="dd-muted mc-carousel-empty">
          No matches — try a shorter search term or browse photos in Brand Library.
        </p>
      )}
      <div className="mc-carousel" role="list">
        {(items || []).map((p) => {
          const id = p.media_asset_id || p.id;
          const sel = id === selectedId;
          return (
            <button
              type="button"
              key={id}
              role="listitem"
              className={`dd-media-card mc-carousel-card${sel ? ' selected' : ''}`}
              onClick={() => onSelect(id, p)}
              title={p.description || p.file_name}
            >
              <div className={`dd-media-thumb${isTransparentAsset(p) ? ' mc-checkerboard' : ''}`}>
                {mediaAssetPreviewUrl(p) ? (
                  <img src={mediaAssetPreviewUrl(p)} alt="" loading="lazy" />
                ) : (
                  <span>No preview</span>
                )}
                <div className="dd-media-badges">
                  {p.score != null && (
                    <span className="dd-badge muted">{Math.round(p.score * 100)}%</span>
                  )}
                  {role === 'photo' && p.slot_dpi ? (
                    <span className={`dd-badge dpi-${dpiClass(p.slot_dpi)}`}>{p.slot_dpi} dpi</span>
                  ) : null}
                  {p.is_vector && <span className="dd-badge cover">SVG</span>}
                  {p.svg_background_removed && (
                    <span className="dd-badge muted" title="Full-artboard background removed from the source SVG">
                      Background removed
                    </span>
                  )}
                </div>
              </div>
              <span className="dd-media-name">{p.file_name}</span>
            </button>
          );
        })}
      </div>
      {onSearchMore && (
        <button type="button" className="dd-link-button mc-carousel-more" onClick={onSearchMore}>
          Search more…
        </button>
      )}
    </div>
  );
};

export default MediaAssetCarousel;
